import React, { useContext, useEffect, useState } from 'react';
import { Button, Chip, CircularProgress, Grid, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';
import MUIDataTable from 'mui-datatables';
import { useSnackbar } from 'notistack';
import _ from 'lodash';
import moment from 'moment';
import KeyboardArrowDownRoundedIcon from '@material-ui/icons/KeyboardArrowDownRounded';
import AppContext from '../../Services/AppContext';
import PersistantData from '../../Services/PersistantData';
import API_Service, { axiosDefaultConfig, axiosURIencode } from '../../Services/API_Service';
import { req_feedback_title, req_feedback_submitted, req_cust, req_stock_brand } from '../../Helper/Constants';
import { useCommonStyles } from '../../Styles';
import LinearLoading from '../../Components/LinearLoading';
import DateRangePickerModal from '../../Components/DateRangePickerModal';
import VirtualListCB from '../../Components/VirtualListCB';
import FeedbackDetailDialog from './components/FeedbackDetailDialog';
import { buildFeedbackFormData } from './exportFormData';

const STATUS_ORDER = { 'pending_resolve': 0, 'resolved': 1 };

const STATUS_CONFIG = {
	'pending_resolve': { bg: 'rgba(230, 81, 0, 0.10)', color: '#ffb74d', label: 'feedback_status_pending_resolve' },
	'resolved': { bg: 'rgba(46, 125, 50, 0.12)', color: '#66bb6a', label: 'feedback_status_resolved' }
};

const STATS_CARDS = [
	{ key: '', labelKey: 'total', color: '#FEF200', bg: 'rgba(254, 242, 0, 0.08)', borderColor: '#FEF200' },
	{ key: 'pending_resolve', labelKey: 'feedback_status_pending_resolve', color: '#ffb74d', bg: 'rgba(230, 81, 0, 0.10)', borderColor: '#ffb74d' },
	{ key: 'resolved', labelKey: 'feedback_status_resolved', color: '#66bb6a', bg: 'rgba(46, 125, 50, 0.12)', borderColor: '#66bb6a' }
];

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(1),
		overflow: 'hidden'
	},
	statsRow: {
		marginBottom: theme.spacing(2)
	},
	statsCard: {
		borderRadius: 10,
		padding: theme.spacing(2),
		minHeight: 76,
		cursor: 'pointer',
		border: '1px solid transparent',
		transition: 'all 0.2s ease',
		'&:hover': {
			transform: 'translateY(-1px)',
			boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
		}
	},
	statsCardTotal: {
		backgroundColor: 'rgba(254, 242, 0, 0.08)',
		'&$statsCardHighlight': {
			boxShadow: 'inset 0 0 0 2px #FEF200'
		}
	},
	statsCardPendingResolve: {
		backgroundColor: 'rgba(230, 81, 0, 0.10)',
		'&$statsCardHighlight': {
			boxShadow: 'inset 0 0 0 2px #ffb74d'
		}
	},
	statsCardResolved: {
		backgroundColor: 'rgba(46, 125, 50, 0.12)',
		'&$statsCardHighlight': {
			boxShadow: 'inset 0 0 0 2px #66bb6a'
		}
	},
	statsCardHighlight: {},
	statsCardHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: theme.spacing(1)
	},
	statsCardCount: {
		fontSize: 28,
		fontWeight: 700
	},
	statsCardCountTotal: { color: '#FEF200' },
	statsCardCountPendingResolve: { color: '#ffb74d' },
	statsCardCountResolved: { color: '#66bb6a' },
	statsCardLabel: {
		fontSize: 11,
		fontWeight: 500,
		textTransform: 'uppercase',
		letterSpacing: 0.5,
		color: theme.palette.text.secondary
	},
	statsCardLabelTotal: { color: 'rgba(254, 242, 0, 0.7)' },
	statsCardLabelPendingResolve: { color: 'rgba(255, 183, 77, 0.8)' },
	statsCardLabelResolved: { color: 'rgba(102, 187, 106, 0.8)' },
	filterPaper: {
		padding: theme.spacing(2),
		marginBottom: theme.spacing(2),
		borderRadius: 10,
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.paper,
		overflow: 'hidden'
	},
	filterBtn: {
		width: '100%',
		maxWidth: '100%',
		minWidth: 0,
		height: 40,
		justifyContent: 'flex-start',
		textTransform: 'none',
		overflow: 'hidden'
	},
	filterBtnText: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		width: '100%',
		textAlign: 'left'
	},
	dateBtn: {
		width: '100%',
		height: 40,
		justifyContent: 'flex-start',
		textTransform: 'none',
		borderColor: theme.palette.divider
	},
	viewBtn: {
		width: '100%',
		height: 40,
		fontWeight: 600,
		textTransform: 'none',
		boxShadow: 'none',
		'&:hover': {
			boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
		}
	},
	tableWrapper: {
		'& .MuiPaper-root': {
			borderRadius: 10,
			boxShadow: 'none',
			border: `1px solid ${theme.palette.divider}`
		},
		'& .MuiTableRow-root': {
			cursor: 'pointer',
			transition: 'background-color 0.15s ease',
			'&:hover': {
				backgroundColor: theme.palette.action.hover
			}
		}
	},
	statusChip: {
		fontWeight: 500,
		fontSize: 12,
		borderRadius: 6,
		height: 24
	},
	statusChipPendingResolve: {
		backgroundColor: 'rgba(230, 81, 0, 0.10)',
		color: '#ffb74d'
	},
	statusChipResolved: {
		backgroundColor: 'rgba(46, 125, 50, 0.12)',
		color: '#66bb6a'
	},
	customerSecondary: {
		fontSize: 11,
		color: theme.palette.text.secondary
	},
	dateSecondary: {
		fontSize: 11,
		color: theme.palette.text.secondary
	},
	skeletonCard: {
		borderRadius: 10
	},
	skeletonFilter: {
		marginTop: theme.spacing(2),
		borderRadius: 10
	},
	skeletonTable: {
		marginTop: theme.spacing(2),
		borderRadius: 10
	}
}));

const CARD_CLASS_MAP = {
	'': { card: 'statsCardTotal', count: 'statsCardCountTotal', label: 'statsCardLabelTotal' },
	'pending_resolve': { card: 'statsCardPendingResolve', count: 'statsCardCountPendingResolve', label: 'statsCardLabelPendingResolve' },
	'resolved': { card: 'statsCardResolved', count: 'statsCardCountResolved', label: 'statsCardLabelResolved' }
};

const STATUS_CHIP_MAP = {
	'pending_resolve': 'statusChipPendingResolve',
	'resolved': 'statusChipResolved'
};

export default function Feedback() {
	const classes = useStyles();
	const commonStyle = useCommonStyles();
	const singleton = PersistantData.getInstance();
	const [gState] = useContext(AppContext);
	var { translation: language, client } = gState;
	if (typeof (language) === 'undefined') {
		language = singleton.getlang();
	}

	const { enqueueSnackbar } = useSnackbar();

	const [loading, setLoading] = useState(true);
	const [tableLoading, setTableLoading] = useState(false);
	const [feedbackList, setFeedbackList] = useState([]);
	const [titleList, setTitleList] = useState([]);
	const [outletList, setOutletList] = useState([]);
	const [selectedTitle, setSelectedTitle] = useState('');
	const [selectedTitleName, setSelectedTitleName] = useState('');
	const [titleView, setTitleView] = useState(false);
	const [selectedOutlet, setSelectedOutlet] = useState('');
	const [selectedOutletName, setSelectedOutletName] = useState('');
	const [outletView, setOutletView] = useState(false);
	const [brandList, setBrandList] = useState([]);
	const [selectedBrand, setSelectedBrand] = useState('');
	const [selectedBrandName, setSelectedBrandName] = useState('');
	const [brandView, setBrandView] = useState(false);
	const [selectedStatus, setSelectedStatus] = useState('');
	const [dateRange, setDateRange] = useState({});
	const [openDatePicker, setOpenDatePicker] = useState(false);
	const [detailOpen, setDetailOpen] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [statsCounts, setStatsCounts] = useState({ total: 0, pending_resolve: 0, resolved: 0 });
	const [tableVisible, setTableVisible] = useState(false);
	const [exporting, setExporting] = useState(false);

	useEffect(() => {
		if (client) {
			loadFeedbackTitles();
			loadOutletList();
			loadBrandList();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [client]);

	const snack = (msg, type = 'success') => {
		enqueueSnackbar(msg, {
			variant: type,
			preventDuplicate: true,
			autoHideDuration: type === 'success' ? 2000 : 3000
		});
	};

	const loadFeedbackTitles = () => {
		API_Service.get(req_feedback_title, axiosDefaultConfig(client)).then(({ data }) => {
			const { result, extraData } = data;
			if (parseInt(result) === 1) {
				const activeTitles = extraData.filter(item => parseInt(item.status) === 1);
				const mapped = activeTitles.map(item => ({
					name: item.title,
					secondary: '',
					value: item.id,
					status: 1
				}));
				setTitleList(mapped);
			} else {
				snack(data.message || 'Error', 'error');
			}
			setLoading(false);
		}).catch(() => {
			snack('Network error', 'error');
			setLoading(false);
		});
	};

	const loadOutletList = () => {
		var parameters = '';
		if (gState.company) {
			const param = axiosURIencode({ company: gState.company });
			parameters = `?${param}`;
		}
		API_Service.get(`${req_cust}${parameters}`, axiosDefaultConfig(client)).then(({ data }) => {
			const { result, extraData } = data;
			if (parseInt(result) === 1) {
				var arr = [];
				var { customer, inactive_customer } = extraData;
				for (let i = 0; i < (customer || []).length; i++) {
					const { cust_code, cust_company_name } = customer[i];
					arr.push({ name: cust_company_name, secondary: cust_code, value: cust_code, status: 1 });
				}
				for (let i = 0; i < (inactive_customer || []).length; i++) {
					const { cust_code, cust_company_name } = inactive_customer[i];
					arr.push({ name: cust_company_name, secondary: cust_code, value: cust_code, status: 0 });
				}
				setOutletList(arr);
			} else {
				snack(data.message || 'Error', 'error');
			}
		}).catch(() => snack('Network error', 'error'));
	};

	const loadBrandList = () => {
		API_Service.get(req_stock_brand, axiosDefaultConfig(client)).then(({ data }) => {
			if (!_.isEmpty(data)) {
				const arr = data.map(item => ({
					name: item.brand_name,
					secondary: item.brand_code,
					value: item.brand_code,
					status: parseInt(item.brand_status) === 1 ? 1 : 0
				}));
				setBrandList(arr);
			}
		}).catch(() => snack('Network error', 'error'));
	};

	const handleTitleClose = (data) => {
		setTitleView(false);
		if (data && data.length > 0) {
			setSelectedTitle(data.map(d => d.value).join(','));
			if (data.length === 1) {
				setSelectedTitleName(data[0].name);
			} else {
				setSelectedTitleName(`${data.length} ${language.feedback_title} ${language.selected}`);
			}
		} else if (data && data.value === '') {
			setSelectedTitle('');
			setSelectedTitleName('');
		}
	};

	const handleOutletClose = (data) => {
		setOutletView(false);
		if (data && data.length > 0) {
			setSelectedOutlet(data.map(d => d.value).join(','));
			if (data.length === 1) {
				setSelectedOutletName(data[0].name);
			} else {
				setSelectedOutletName(`${data.length} ${language.customer_low} ${language.selected}`);
			}
		} else if (data && data.value === '') {
			setSelectedOutlet('');
			setSelectedOutletName('');
		}
	};

	const handleBrandClose = (data) => {
		setBrandView(false);
		if (data && data.length > 0) {
			setSelectedBrand(data.map(d => d.value).join(','));
			if (data.length === 1) {
				setSelectedBrandName(data[0].name);
			} else {
				setSelectedBrandName(`${data.length} ${language.brand} ${language.selected}`);
			}
		} else if (data && data.value === '') {
			setSelectedBrand('');
			setSelectedBrandName('');
		}
	};

	const handleView = (statusOverride) => {
		const effectiveStatus = statusOverride !== undefined ? statusOverride : selectedStatus;
		const formdata = buildFeedbackFormData({
			selectedTitle,
			selectedOutlet,
			selectedBrand,
			status: effectiveStatus,
			dateRange
		});

		setTableLoading(true);
		API_Service.post(req_feedback_submitted, formdata, axiosDefaultConfig(client)).then(({ data }) => {
			if (parseInt(data.result) === 1) {
				const sorted = [...data.extraData].sort((a, b) => {
					const orderA = STATUS_ORDER[a.status] !== undefined ? STATUS_ORDER[a.status] : 3;
					const orderB = STATUS_ORDER[b.status] !== undefined ? STATUS_ORDER[b.status] : 3;
					return orderA - orderB;
				});
				setFeedbackList(sorted);
				if (!effectiveStatus) {
					const items = data.extraData;
					setStatsCounts({
						total: items.length,
						pending_resolve: items.filter(f => f.status === 'pending_resolve').length,
						resolved: items.filter(f => f.status === 'resolved').length
					});
				}
			} else {
				setFeedbackList([]);
				snack(data.message || language.no_data, 'warning');
			}
			setTableLoading(false);
		}).catch(() => {
			snack('Network error', 'error');
			setTableLoading(false);
		});
	};

	const handleExport = () => {
		const formdata = buildFeedbackFormData({
			selectedTitle,
			selectedOutlet,
			selectedBrand,
			status: selectedStatus,
			dateRange
		});
		formdata.append('export', 'true');

		setExporting(true);
		API_Service.post(req_feedback_submitted, formdata, axiosDefaultConfig(client)).then(({ data }) => {
			if (parseInt(data.result) === 1 && data.extraData && data.extraData.url) {
				const link = document.createElement('a');
				link.href = data.extraData.url;
				link.download = data.extraData.filename || 'feedback_listing_report.xlsx';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				snack(language.download_success);
			} else {
				snack(data.message || language.no_data, 'warning');
			}
			setExporting(false);
		}).catch(() => {
			snack('Network error', 'error');
			setExporting(false);
		});
	};

	const getDateDisplay = () => {
		if (!_.isEmpty(dateRange)) {
			return moment(dateRange.startDate).format('DD MMMM YYYY') + ' / ' + moment(dateRange.endDate).format('DD MMMM YYYY');
		}
		return moment(new Date()).format('DD MMMM YYYY') + ' / ' + moment(new Date()).format('DD MMMM YYYY');
	};

	const getCardCount = (key) => {
		if (key === '') return statsCounts.total;
		return statsCounts[key] || 0;
	};

	const handleStatsCardClick = (statusKey) => {
		setSelectedStatus(statusKey);
		handleView(statusKey);
	};

	const columns = [
		{
			name: 'feedback_title_name',
			label: language.feedback_title,
			options: { display: true }
		},
		{
			name: 'cust_company_name',
			label: language.customer_low,
			options: {
				display: true,
				customBodyRender: (value, tableMeta) => {
					const custCode = feedbackList[tableMeta.rowIndex]?.cust_code;
					return (
						<div>
							<Typography variant="body2">{value}</Typography>
							{custCode && <Typography className={classes.customerSecondary}>{custCode}</Typography>}
						</div>
					);
				}
			}
		},
		{
			name: 'brand_code',
			label: language.brand_code,
			options: { display: true }
		},
		{
			name: 'description',
			label: language.feedback_description,
			options: {
				display: true,
				customBodyRender: (value) => (
					<Typography noWrap variant="body2">{_.truncate(value, { length: 60 })}</Typography>
				)
			}
		},
		{
			name: 'status',
			label: language.status,
			options: {
				display: true,
				customBodyRender: (value) => {
					const config = STATUS_CONFIG[value] || { label: value };
					const chipClass = STATUS_CHIP_MAP[value] || '';
					return (
						<Chip
							size="small"
							label={language[config.label] || value}
							className={`${classes.statusChip} ${chipClass ? classes[chipClass] : ''}`}
						/>
					);
				}
			}
		},
		{
			name: 'created_by_name',
			label: language.salesperson,
			options: { display: true }
		},
		{
			name: 'created_at',
			label: language.date,
			options: {
				display: true,
				customBodyRender: (value) => {
					const m = moment(value, 'YYYY-MM-DD HH:mm:ss');
					return (
						<div>
							<Typography variant="body2">{m.format('DD/MM/YYYY')}</Typography>
							<Typography className={classes.dateSecondary}>{m.format('HH:mm')}</Typography>
						</div>
					);
				}
			}
		}
	];

	const options = {
		selectableRows: 'none',
		responsive: 'standard',
		rowsPerPage: 20,
		rowsPerPageOptions: [20, 50, 100],
		download: false,
		print: false,
		filter: false,
		search: true,
		onRowClick: (rowData, rowMeta) => {
			setSelectedIndex(rowMeta.dataIndex);
			setDetailOpen(true);
		}
	};

	if (loading) {
		return (
			<div className={classes.root}>
				<Grid container spacing={2}>
					{[0, 1, 2].map(i => (
						<Grid item xs={6} sm={4} key={i}>
							<Skeleton animation="wave" variant="rect" height={76} className={classes.skeletonCard} />
						</Grid>
					))}
				</Grid>
				<Skeleton animation="wave" height={60} className={classes.skeletonFilter} />
				<Skeleton animation="wave" height={300} className={classes.skeletonTable} />
			</div>
		);
	}

	return (
		<div className={classes.root}>
			<Grid container spacing={2} className={classes.statsRow}>
				{STATS_CARDS.map((stat) => {
					const cardClasses = CARD_CLASS_MAP[stat.key];
					const isHighlighted = selectedStatus === stat.key;
					return (
						<Grid item xs={6} sm={4} key={stat.key}>
							<Paper
								className={`${classes.statsCard} ${classes[cardClasses.card]} ${isHighlighted ? classes.statsCardHighlight : ''}`}
								onClick={() => handleStatsCardClick(stat.key)}
								elevation={0}
							>
								<Typography className={`${classes.statsCardLabel} ${classes[cardClasses.label]}`}>
									{language[stat.labelKey]}
								</Typography>
								<Typography className={`${classes.statsCardCount} ${classes[cardClasses.count]}`}>
									{getCardCount(stat.key)}
								</Typography>
							</Paper>
						</Grid>
					);
				})}
			</Grid>

			<Paper variant="outlined" className={classes.filterPaper}>
				<Grid container spacing={1} alignItems="center">
					<Grid item xs={12} sm={3}>
						<Button
							className={classes.filterBtn}
							variant="outlined"
							size="small"
							onClick={() => setTitleView(true)}
							endIcon={<KeyboardArrowDownRoundedIcon />}>
							<Typography className={`${commonStyle.searchBtnFont} ${classes.filterBtnText}`}>
								{selectedTitleName || language.feedback_title}
							</Typography>
						</Button>
					</Grid>

					<Grid item xs={12} sm={3}>
						<Button
							className={classes.filterBtn}
							variant="outlined"
							size="small"
							onClick={() => setOutletView(true)}
							endIcon={<KeyboardArrowDownRoundedIcon />}>
							<Typography className={`${commonStyle.searchBtnFont} ${classes.filterBtnText}`}>
								{selectedOutletName || language.customer_low}
							</Typography>
						</Button>
					</Grid>

					<Grid item xs={12} sm={2}>
						<Button
							className={classes.filterBtn}
							variant="outlined"
							size="small"
							onClick={() => setBrandView(true)}
							endIcon={<KeyboardArrowDownRoundedIcon />}>
							<Typography className={`${commonStyle.searchBtnFont} ${classes.filterBtnText}`}>
								{selectedBrandName || language.brand}
							</Typography>
						</Button>
					</Grid>

					<Grid item xs={12} sm={2}>
						<Button
							className={classes.dateBtn}
							variant="outlined"
							size="small"
							onClick={() => setOpenDatePicker(true)}>
							<Typography className={commonStyle.searchBtnFont}>
								{getDateDisplay()}
							</Typography>
						</Button>
					</Grid>

					<Grid item xs={12} sm={1}>
						<Button
							size="small"
							color="primary"
							variant="contained"
							className={classes.viewBtn}
							onClick={() => { setSelectedStatus(''); setTableVisible(true); handleView(''); }}>
							{language.view}
						</Button>
					</Grid>

					<Grid item xs={12} sm={1}>
						<Button
							size="small"
							color="primary"
							variant="contained"
							className={classes.viewBtn}
							disabled={exporting}
							onClick={handleExport}>
							{exporting ? <CircularProgress size={20} /> : language.export}
						</Button>
					</Grid>
				</Grid>
			</Paper>

			{tableLoading && <LinearLoading />}
			{tableVisible && (
				<div className={classes.tableWrapper}>
					<MUIDataTable
						title={language.feedback_listing}
						data={feedbackList}
						columns={columns}
						options={options}
					/>
				</div>
			)}

			<VirtualListCB
				open={titleView}
				setClose={handleTitleClose}
				title={language.feedback_title}
				data={titleList}
				language={language}
				filteration={1}
			/>

			<VirtualListCB
				open={outletView}
				setClose={handleOutletClose}
				title={language.customer_low}
				data={outletList}
				language={language}
				filteration={1}
			/>

			<VirtualListCB
				open={brandView}
				setClose={handleBrandClose}
				title={language.brand}
				data={brandList}
				language={language}
				filteration={1}
			/>

			<DateRangePickerModal
				setPositive={(range) => {
					setDateRange(range);
					setOpenDatePicker(false);
				}}
				setNegative={() => {
					setOpenDatePicker(false);
				}}
				setClose={() => {
					setOpenDatePicker(false);
				}}
				open={openDatePicker}
				message={language.date_range}
				positive={language.ok}
			/>

			<FeedbackDetailDialog
				open={detailOpen}
				onClose={() => setDetailOpen(false)}
				feedbackList={feedbackList}
				selectedIndex={selectedIndex}
				onStatusUpdated={(oldStatus, newStatus) => {
					setStatsCounts(prev => ({
						...prev,
						[oldStatus]: Math.max(0, (prev[oldStatus] || 0) - 1),
						[newStatus]: (prev[newStatus] || 0) + 1
					}));
					handleView(selectedStatus);
				}}
			/>
		</div>
	);
}
